"use client";
import { useEffect, useRef, useState } from "react";

/**
 * Marquee — endlose horizontale Scroll-Bahn (Locations).
 *
 * A11y: gesamtes Marquee-Track ist aria-hidden=true (rein dekorativ).
 * Die echte Liste der Locations wird stattdessen als visuell verborgene,
 * aber screenreader-lesbare <ul> außerhalb des Tracks gerendert. Damit
 * landen die 22 Standorte EINMAL im AT, statt N-fach durch die Loop.
 *
 * Region-Marker: Items mit Präfix "—" (z.B. "—NORD—") werden als
 * Eyebrow-Style gerendert (fcp-green, uppercase, letter-spacing). Marker
 * sind keine echten Standorte und werden aus der SR-Liste ausgeschlossen.
 *
 * Pausiert bei prefers-reduced-motion, bei :hover (CSS) und wenn der
 * Tab im Hintergrund liegt (visibilitychange).
 */
export function Marquee({
  items,
  speed = 50,
  separator,
  direction = "left",
}: {
  items: ReadonlyArray<string>;
  speed?: number; // px pro Sekunde
  separator?: React.ReactNode;
  /**
   * Scroll-Richtung. "left" (default) = klassisch nach links;
   * "right" = umgekehrt. Implementiert über CSS custom property
   * --marquee-direction, die `animation-direction` flippt.
   */
  direction?: "left" | "right";
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setEnabled(!prefersReduce);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const track = trackRef.current;
    if (!track) return;

    const half = track.scrollWidth / 2; // Track ist verdoppelt
    if (!half) return;
    const duration = half / speed; // Sekunden
    track.style.setProperty("--mq-duration", `${duration}s`);
    track.style.setProperty("--mq-distance", `${-half}px`);

    // Pause wenn Tab inaktiv -> spart CPU/GPU
    const onVis = () => {
      track.style.animationPlayState = document.hidden ? "paused" : "running";
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [enabled, speed, items]);

  const doubled = [...items, ...items];

  return (
    <div
      className="lp-marquee"
      aria-hidden="true"
      style={{ ["--marquee-direction" as any]: direction === "right" ? "reverse" : "normal" }}
    >
      <div
        ref={trackRef}
        className={`lp-marquee-track${enabled ? " is-running" : ""}`}
      >
        {doubled.map((label, i) => (
          <span className="lp-marquee-item" key={`${label}-${i}`}>
            {label}
            {separator && <span className="lp-marquee-sep">{separator}</span>}
          </span>
        ))}
      </div>
      {/* Screenreader-only Liste der ECHTEN Standorte (nicht im Loop). */}
      <ul
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {items.map((label) => (
          <li key={label}>{label}</li>
        ))}
      </ul>
    </div>
  );
}
